import React, { useCallback, useEffect, useState } from 'react';
import { Card, Form, InputNumber, Button, Space, Typography, message, Skeleton, Divider, Select } from 'antd';
import { AppstoreOutlined, ReloadOutlined, SaveOutlined, FileOutlined } from '@ant-design/icons';
import MainLayout from '../../../layouts/MainLayout';
import { superadminAppSettingsApi, show422Error } from '../../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../../constants/ui';

const { Text } = Typography;

const parseFileTypes = (raw) => {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) {
    return raw.map((item) => (typeof item === 'object' && item?.value != null ? String(item.value) : String(item))).filter(Boolean);
  }
  return String(raw)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
};

const toApiFileTypes = (values) => (values || []).map((v) => ({ value: String(v).trim() })).filter((x) => x.value);

const SuperadminAppSettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await superadminAppSettingsApi.getFileUpload();
      const data = res?.data || {};
      const types = parseFileTypes(data.allowed_file_types);
      form.setFieldsValue({
        allowed_file_size: Number(data.allowed_file_size) || 10,
        allow_max_no_of_files: Number(data.allow_max_no_of_files) || 10,
        allowed_file_types: types,
      });
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت تنظیمات');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      await superadminAppSettingsApi.updateFileUpload({
        allowed_file_size: Number(values.allowed_file_size),
        allow_max_no_of_files: Number(values.allow_max_no_of_files),
        allowed_file_types: toApiFileTypes(values.allowed_file_types),
      });
      message.success('تنظیمات ذخیره شد');
      fetchData();
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, form);
      else message.error(error?.message || 'خطا در ذخیره');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MainLayout>
      <div className="custom-font w-full min-w-0" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-11 w-11 sm:h-12 sm:w-12 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-700/50">
                <AppstoreOutlined className="text-lg sm:text-xl text-slate-600 dark:text-slate-300" />
              </div>
              <div className="min-w-0">
                <h1 className="m-0 text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100">تنظیمات برنامه</h1>
                <Text type="secondary" className="text-xs sm:text-sm">
                  محدودیت‌ها و انواع فایل مجاز برای آپلود
                </Text>
              </div>
            </div>
            <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
              بروزرسانی
            </Button>
          </div>

          <div className="rounded-b-xl bg-gray-50 p-4 sm:p-6 dark:bg-gray-900/50">
            <Card className="border border-gray-100 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              {loading ? (
                <Skeleton active paragraph={{ rows: 8 }} />
              ) : (
                <Form form={form} layout="vertical" onFinish={onSubmit}>
                  <div className="mb-4 flex items-start gap-2 rounded-lg border border-blue-100 bg-blue-50/80 p-3 text-sm text-blue-800 dark:border-blue-900/40 dark:bg-blue-900/20 dark:text-blue-200">
                    <FileOutlined className="mt-0.5 flex-shrink-0" />
                    <span>هر نوع MIME یا پسوند را می‌توانید اضافه کنید؛ مثال: image/* یا application/pdf</span>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Form.Item
                      name="allowed_file_size"
                      label="حداکثر حجم هر فایل (مگابایت)"
                      rules={[{ required: true, message: 'مقدار را وارد کنید' }]}
                    >
                      <InputNumber min={1} max={500} className="w-full" />
                    </Form.Item>
                    <Form.Item
                      name="allow_max_no_of_files"
                      label="حداکثر تعداد فایل"
                      rules={[{ required: true, message: 'مقدار را وارد کنید' }]}
                    >
                      <InputNumber min={1} max={100} className="w-full" />
                    </Form.Item>
                  </div>

                  <Form.Item
                    name="allowed_file_types"
                    label="انواع فایل مجاز"
                    rules={[{ required: true, message: 'حداقل یک نوع فایل وارد کنید' }]}
                    extra="برای افزودن، تایپ کنید و Enter بزنید"
                  >
                    <Select
                      mode="tags"
                      className="w-full"
                      popupClassName="custom-font"
                      placeholder="مثال: image/*"
                      tokenSeparators={[',']}
                    />
                  </Form.Item>

                  <Divider />
                  <Form.Item className="mb-0">
                    <Space wrap>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={submitting}
                        icon={<SaveOutlined />}
                        className={PANEL_PRIMARY_BTN_CLASS}
                      >
                        ذخیره تغییرات
                      </Button>
                    </Space>
                  </Form.Item>
                </Form>
              )}
            </Card>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default SuperadminAppSettings;
